import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { KundenService } from '../services/kundenService';
import { NutzerService } from '../services/nutzerService';
import { ZulassungsantragService } from '../services/zulassungsantragService';

interface DashboardStats {
  totalKunden: number;
  totalNutzer: number;
  totalZulassungsantraege: number;
  loading: boolean;
  error: string | null;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalKunden: 0,
    totalNutzer: 0,
    totalZulassungsantraege: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true, error: null }));

      // Parallel laden der Dashboard-Daten
      const [kundenResponse, nutzerResponse, antraegeResponse] = await Promise.all([
        KundenService.getAlleKunden(0, 1), // Nur erste Seite für Total-Count
        NutzerService.getAlleNutzer(0, 1),   // Nur erste Seite für Total-Count
        ZulassungsantragService.getAlleZulassungsantraege(0, 1) // Nur erste Seite für Total-Count
      ]);

      setStats({
        totalKunden: kundenResponse.totalElements,
        totalNutzer: nutzerResponse.totalElements,
        totalZulassungsantraege: antraegeResponse.totalElements,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Fehler beim Laden der Dashboard-Daten:', error);
      setStats(prev => ({
        ...prev,
        loading: false,
        error: 'Fehler beim Laden der Dashboard-Daten'
      }));
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <Card elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" color={color}>
              {stats.loading ? <CircularProgress size={24} /> : value}
            </Typography>
          </Box>
          <Box color={color} sx={{ fontSize: 40 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (stats.error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {stats.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        GKS Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Übersicht über die Großkundenschnittstelle für Fahrzeugzulassung
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Kunden"
            value={stats.totalKunden}
            icon={<BusinessIcon />}
            color="primary.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nutzer"
            value={stats.totalNutzer}
            icon={<PeopleIcon />}
            color="secondary.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Zulassungsanträge"
            value={stats.totalZulassungsantraege}
            icon={<AssignmentIcon />}
            color="success.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Aktive Prozesse"
            value={0}
            icon={<TrendingUpIcon />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Weitere Dashboard-Inhalte können hier hinzugefügt werden */}
      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Letzte Aktivitäten
              </Typography>
              <Typography color="textSecondary">
                Hier werden die neuesten Aktivitäten angezeigt...
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System-Status
              </Typography>
              <Typography color="textSecondary">
                Alle Systeme funktionieren ordnungsgemäß ✅
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
